/**
 * 전체 학생 현황 탭 (2026-09-04, 부천신중동점 원장 요청)
 *
 * "전체 학생의 순공시간, 벌점, 출석 횟수를 한눈에"
 * "월간 순공시간·출석시간을 정렬할 수 있으면 빠르게 순위를 알 수 있겠다.
 *  지금은 한 사람 한 사람 다 들어가봐야 한다"
 *
 * 그 말이 사실이었다 — 회원 목록에도, 학습 분석에도 전체를 훑는 방법이 없었다.
 *
 * ⚠️ 옆의 '주간 순공 랭킹'과 다른 것이다. 그쪽은 마감 시각에 **동결 저장**한 값이고
 *    이 표는 **볼 때마다 다시 계산**한다. 같은 주라도 숫자가 다를 수 있어 안내문을 넣었다.
 * ⚠️ 그 기간에 한 번도 안 온 학생도 0으로 나온다 — 원장이 보려는 것 중 하나가 그것이다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';

interface StudentStat {
  student_id: string;
  name: string;
  study_minutes: number;
  attend_days: number;
  demerit: number;
  merit: number;
}

interface PeriodInfo {
  key: string;
  label: string;
  start: string;
  end: string;
}

const getApiUrl = (path: string) => {
  const baseUrl = API_BASE_URL === '' ? '/api' : API_BASE_URL;
  return `${baseUrl}${path}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const PERIODS = [
  { key: 'this_week', label: '이번 주' },
  { key: 'this_month', label: '이번 달' },
  { key: 'last_month', label: '지난 달' },
];

type SortKey = 'study_minutes' | 'attend_days' | 'demerit' | 'merit' | 'name';

const COLUMNS: { key: SortKey; label: string; align: string }[] = [
  { key: 'name', label: '이름', align: 'text-left' },
  { key: 'study_minutes', label: '순공시간', align: 'text-right' },
  { key: 'attend_days', label: '출석일수', align: 'text-right' },
  { key: 'demerit', label: '벌점', align: 'text-right' },
  { key: 'merit', label: '상점', align: 'text-right' },
];

const formatMinutes = (mins: number) => {
  if (!mins) return '-';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}시간 ${m}분` : `${m}분`;
};

export const StudentStatsTab: React.FC = () => {
  const [period, setPeriod] = useState('this_month');
  const [rows, setRows] = useState<StudentStat[]>([]);
  const [info, setInfo] = useState<PeriodInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('study_minutes');
  const [asc, setAsc] = useState(false);
  const [keyword, setKeyword] = useState('');

  const load = useCallback(async (p: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(`/v1/owner/study/student-stats?period=${p}`), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`조회 실패 (${res.status})`);
      const data = await res.json();
      setRows(data.students || []);
      setInfo(data.period || null);
    } catch (e: any) {
      setError(e.message || '학생 현황을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  // 제목을 누르면 그 기준으로 정렬. 같은 걸 다시 누르면 반대 방향.
  // 이름만 오름차순이 기본이고(가나다), 숫자는 큰 값이 위로 오는 게 자연스럽다.
  const toggleSort = (key: SortKey) => {
    if (key === sortKey) { setAsc(!asc); return; }
    setSortKey(key);
    setAsc(key === 'name');
  };

  const view = useMemo(() => {
    const kw = keyword.trim();
    const filtered = kw ? rows.filter(r => r.name.includes(kw)) : rows;
    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name, 'ko');
      return (a[sortKey] as number) - (b[sortKey] as number);
    });
    return asc ? sorted : sorted.reverse();
  }, [rows, sortKey, asc, keyword]);

  const totals = useMemo(() => {
    const studied = rows.filter(r => r.study_minutes > 0).length;
    return { studied, none: rows.length - studied };
  }, [rows]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">📋 전체 학생 현황</h3>
        <p className="mt-1 text-sm text-gray-600">
          기간을 고르고 <span className="font-medium">제목을 누르면</span> 그 기준으로 순위가 정렬됩니다.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              period === p.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {p.label}
          </button>
        ))}
        <input
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          placeholder="이름 검색"
          className="ml-auto px-3 py-1.5 border border-gray-300 rounded-md text-sm w-40"
        />
      </div>

      {info && (
        <div className="text-sm text-gray-600">
          <span className="font-medium">{info.label}</span> ({info.start} ~ {info.end}) ·
          재원 {rows.length}명 중 <span className="font-medium">{totals.studied}명</span> 출석
          {totals.none > 0 && <span className="text-red-600"> · 기록 없음 {totals.none}명</span>}
        </div>
      )}

      {/* 옆 탭(주간 순공 랭킹)은 마감 시각에 동결한 값이라 숫자가 다를 수 있다.
          안 적어두면 "왜 다르냐"는 문의가 온다. */}
      <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
        지금 이 순간 기준으로 다시 계산한 값입니다. 🏆 주간 순공 랭킹 탭은 마감 시각에
        기록을 얼려 저장한 것이라 같은 주라도 숫자가 다를 수 있습니다.
        <br />
        출석일수는 <span className="font-medium">공부 시간이 기록된 날</span>만 셉니다 —
        퇴실을 찍지 않고 간 날은 시간이 0으로 남아 빠집니다.
        (그런 날은 [운영 관리] → [퇴실 미기록 확인]에서 바로잡을 수 있습니다.)
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">불러오는 중…</div>
      ) : error ? (
        <div className="py-8 text-center text-red-600 text-sm">{error}</div>
      ) : view.length === 0 ? (
        <div className="py-12 text-center text-gray-500">표시할 학생이 없습니다.</div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-gray-500 font-medium w-12">순위</th>
                {COLUMNS.map(c => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    className={`px-3 py-2 ${c.align} text-gray-700 font-medium cursor-pointer select-none hover:bg-gray-100`}
                  >
                    {c.label}
                    {sortKey === c.key && <span className="ml-1">{asc ? '▲' : '▼'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {view.map((s, i) => (
                <tr key={s.student_id} className={s.study_minutes === 0 ? 'bg-red-50/40' : 'hover:bg-gray-50'}>
                  <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{s.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMinutes(s.study_minutes)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.attend_days ? `${s.attend_days}일` : '-'}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${s.demerit > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                    {s.demerit || '-'}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${s.merit > 0 ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                    {s.merit || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StudentStatsTab;
